/* tslint:disable:typedef-whitespace no-trailing-whitespace no-inferrable-types import-spacing */

import {ElementRef}         from '@angular/core';

import {Dialog}             from './dialog';
import {Logger}             from '@/util/logger';
import {IValidatorCallBack} from './i.validator.call.back';
import {IOkCallBack}        from './i.ok.call.back';
import {BlobType, Blob}     from '@/models/blob';

import * as $               from 'jquery';
import 'jquery-ui-dist/jquery-ui';

/**
 *
 * @class NewDimensionDialog
 *
 * @brief dialog for selecting the kind of dimension to add to the inputs of a simulation.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class NewDimensionDialog extends Dialog {

  /** the name we're trying get from the user */
  private name: string = '';

  /** the type of the dimension */
  private type: BlobType = BlobType.Plant;

  /** the kinds of dimensions we are allowing */
  private allowed: BlobType[] = [BlobType.Plant, BlobType.Traffic];

  /** timer to wait for user to stop typing */
  private typingTimer = null;

  /** standard constructor, allow for partial alert to be given */
  constructor(
    host: ElementRef
  ) {

    super(host);

    this.title = 'New Input Dimension';
    this.icon  = '<i class="fas fa-plus"></i>';

    Logger.debug('[NewDimensionDialog] setting up...');
  }

  /** dialog has been opened */
  public open(validator?: IValidatorCallBack, success?: IOkCallBack, options?: any) {

    this.validator = validator ? validator : null;
    this.success   = success   ? success   : null;

    if (this.typingTimer) {

      clearTimeout(this.typingTimer);

      this.typingTimer = null;
    }

    Logger.debug('[NewDimensionDialog] opening...');

    this.name = '';
    this.type = BlobType.Plant;

    const body = $(this.host).find('.modal-body');

    const html = `
<div class="control gui-radio-toolbar">

  <input id="plant" type="radio" name="type"  value="plant" checked>
  <label for="plant" class="radio" style="margin-right: 12px;">
    <img src="assets/images/blob-icons/blob-plant-20.png" style="margin-right: 8px;">Plant
  </label>

  <input id="traffic" type="radio" name="type"  value="traffic">
  <label for="traffic" class="radio">
    <img src="assets/images/blob-icons/blob-traffic-20.png" style="margin-right: 8px;">Traffic
  </label>

</div>
<div style="margin-top: 12px;">
<label class="gui-label">Name:</label>
<input class="gui-input dimension-name-input" type="text" placeholder="Dimension Name">
</div>
<div class="gui-error-line"></div>
    `;

    $(body).html(html);

    /* add behaviors */

    setTimeout(() => {

      $(this.host).find('.dimension-name-input').focus();

      $('input[type=radio][name=type]').on('input', (e) => {

        this.type = Blob.typeofString($(e.currentTarget).val() as string);

        this.validate();
      });

      $(this.host).find('.dimension-name-input').on('input', (e) => {

        this.error('');

        /* typing wait: @link https://stackoverflow.com/questions/10124114/wait-for-function-till-user-stops-typing */

        if (this.typingTimer) {

          clearTimeout(this.typingTimer);
        }

        this.typingTimer = setTimeout(() => {

          this.name = ($(e.currentTarget).val() as string).trim();

          this.validate();

        }, 400);

      });

      $(this.host).find('.dimension-name-input').on('keypress', (e) => {

        this.error('');

        if (this.typingTimer) {

          clearTimeout(this.typingTimer);
        }

        this.typingTimer = setTimeout(() => {

          this.name = ($(e.currentTarget).val() as string).trim();

          if (e.key.toLowerCase() === 'enter') {

            this.validate(true);

            /* stop event propagation */

            return false;
          }

        }, 400);

      });

    }, 50);

    /* disable the Ok button...until we have valid input */

    this.validate();
  }

  disable() {

    const ok = $(this.host).find('.modal-ok-button');

    $(ok).attr('disabled', 'true');
    $(ok).addClass('disabled');
  }

  enable() {

    const ok = $(this.host).find('.modal-ok-button');

    $(ok).removeAttr('disabled');
    $(ok).removeClass('disabled');

  }

  /** show a validation error */
  error(msg: string) {

    const display = $(this.host).find('.gui-error-line');

    $(display).html(msg);
  }

  /* don't enable the Ok button until we have what we need. */
  validate(transition: boolean = false) {

    this.error('');

    /* assume its bad */

    this.disable();

    if (!this.validator) {

      /* at least make sure the name is't empty */

      if (this.name === '') {

        this.error('Dimension name can not be empty.');

        return ;
      }

      if (this.allowed.find(x => x === this.type)) {

        this.error('Unknown kind of input: ' + Blob.stringOfType(this.type));

        return ;
      }

      this.enable();

      /* if we make it through validiation checks, and we're allowed to transition, then do it. */

      if (transition) {

        /* click the ok button for them, and trigger via ModelComponent */

        const ok = $(this.host).find('.modal-ok-button');

        $(ok).click();
      }

    } else {

      /* wrap validation in a promise, so its ok if its asynchronous */

      const p = new Promise((resolve, reject) => {

        this.validator({name: this.name, type: this.type}, resolve, reject);

      }).then((data: any) => {

        this.name = data.name as string;
        this.type = data.type as BlobType;

        this.enable();

        if (transition) {

          /* click the ok button for them, and trigger via ModelComponent */

          const ok = $(this.host).find('.modal-ok-button');

          $(ok).click();
        }

      }).catch((error) => {

        /* show the validation error */

        this.error(error);

      });

    }

  }

  /** dialog is closing */
  public close() {

    Logger.debug('[NewDimensionDialog] closing...');
  }

  /** dialog was accepted */
  public ok() {

    Logger.debug('[NewDimensionDialog] accepted: ', this.name);

    if (this.success) {

      /* inform the client */

      this.success( {name: this.name, type: this.type});
    }

  }
}


